/** Live count of joined participants relative to the org plan cap.
 *
 * Phase 1B-H-multi. Renders as an inline pill next to the session
 * header — informational at low fill, amber at >=75% cap, red at the
 * cap. Server enforces the cap at SessionJoinView (409
 * reason=participant_cap_exceeded); this just gives the operator a
 * heads-up before they hit it.
 *
 * `max_participants` is the plan ceiling (null = unlimited; we hide
 * the badge entirely in that case). The current count is fetched
 * from the participants list endpoint, polled every 15s — cheap
 * enough not to bother with websockets for an admin-facing display.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";

type ParticipantsResponse = {
  results?: Array<unknown>;
};

type Props = {
  sessionId: string;
  maxParticipants: number | null;
};

export function ParticipantCountBadge({ sessionId, maxParticipants }: Props) {
  const { t } = useTranslation();
  const participants = useQuery({
    queryKey: ["session-participants", sessionId],
    queryFn: async () => {
      const res = await api.get<ParticipantsResponse | unknown[]>(
        ApiPaths.sessionParticipants(sessionId),
      );
      // Endpoint may return a plain array or a paginated envelope.
      if (Array.isArray(res.data)) return res.data.length;
      const r = (res.data as ParticipantsResponse).results;
      return Array.isArray(r) ? r.length : 0;
    },
    refetchInterval: 15_000,
  });

  if (!maxParticipants) return null;
  // +1 to include the operator (operator isn't a Participant row;
  // they live on Session.operator).
  const count = (participants.data ?? 0) + 1;
  const ratio = count / maxParticipants;
  const tone =
    ratio >= 1
      ? "border-cvp-danger bg-cvp-danger/15 text-cvp-danger"
      : ratio >= 0.75
        ? "border-amber-500 bg-amber-500/10 text-amber-200"
        : "border-nb-border bg-nb-surface/40 text-nb-text";

  const label =
    ratio >= 1
      ? t("ui.participantCountBadge.full", { count, max: maxParticipants })
      : t("ui.participantCountBadge.joined", { count, max: maxParticipants });

  return (
    <span
      data-testid="participant-count-badge"
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${tone}`}
      title={t("ui.participantCountBadge.tooltip")}
    >
      {label}
    </span>
  );
}
